import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { OW_API, formatApiErrorDetail } from "../lib/operatorWalk";
import PortalAuthShell from "../components/ow/PortalAuthShell";

export default function PortalReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${OW_API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/portal/login", { replace: true }), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalAuthShell
      title="Set a new password"
      footer={
        <Link to="/portal/login" className="text-brand-700 underline" data-testid="portal-reset-back-link">
          Back to login
        </Link>
      }
    >
      {!token ? (
        <p className="text-sm text-red-700" data-testid="portal-reset-no-token">
          This reset link is missing its token. Please request a new link from the forgot password page.
        </p>
      ) : done ? (
        <div className="text-center" data-testid="portal-reset-done">
          <CheckCircle2 className="w-10 h-10 text-brand-500 mx-auto" />
          <p className="mt-4 text-ink-soft text-sm">Your password is updated. Redirecting you to login.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="portal-reset-form">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">New password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border border-brand-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-500"
              data-testid="portal-reset-password"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Confirm password</label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 w-full border border-brand-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-500"
              data-testid="portal-reset-confirm"
              autoComplete="new-password"
            />
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="portal-reset-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3.5 text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="portal-reset-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Update password
          </button>
        </form>
      )}
    </PortalAuthShell>
  );
}
